import i18n from 'i18n'
import _ from 'lodash'
import { Node, UIText } from 'qc'
import * as languageScript from '@ps/bridge/qc/scripts/language'
import { generateLanguageConfigTextByNode, LanguageScript } from '@ps/bridge/qc/scripts/language'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { getDescendantsText } from '@ps/bridge/qc/node/utils'

function getTextConfigFromLanguagesJson({
  lang,
  languages,
  textKey
}: {
  lang: string;
  languages: i18n.Languages;
  textKey: string;
}) {
  // languages.json 的 key 可能存在 . [ ] ' " 等特殊字符
  if (_.get(languages, [lang, textKey])) {
    return _.get(languages, [lang, textKey])
  }
  console.error(`从 languages.json 中获取 ${textKey} 文本配置失败，改成会从节点中获取`)
}

/**
 * 加工文本节点
 */
export function cookTextNode({
  node,
  langList,
  languagesRes
}: {
  node: Node;
  langList: string[];
  languagesRes: i18n.LanguagesConfig;
}): void {
  const langScript = languageScript.getScript(node, false) as LanguageScript
  const hasLangScript = Boolean(langScript)
  // langConfig 不为空代表是新脚本
  const isNewScript = hasLangScript && !_.isEmpty(langScript.langConfig)
  // 有 langConfig 说明是新脚本，不需要处理（且认为节点上的内容与languages.json的内容都是正确的)
  if (isNewScript) return

  // 旧脚本或者没有脚本
  const editorConfig = editorScript.getScript(node) as EditorDataScript
  const isTempNode = editorConfig.$data.isTempNode
  // 如果非临时节点，则补充必要信息
  if (!isTempNode) {
    editorConfig.$data.isTempNode = false
    // 有可能没有脚本
    if (hasLangScript && langScript.langKey) {
      // 记录导入时该节点的key（这个 key 的作用是读取 languages.json 的配置）
      editorConfig.$data.oldLangKey = langScript.langKey
    } else {
      console.error(`${node.name} 该文本节点没有脚本或脚本没有 langKey，所以无法从 languages.json 中获取到对应的语言配置`, node)
    }
  }
  const oldLangKey = editorConfig.$data.oldLangKey
  // 移除旧脚本
  languageScript.removeScript(node)

  // 为节点新增脚本并获取脚本信息
  const newConfig = languageScript.getScript(node) as LanguageScript
  // 注意：保证 langKey 与原来的一致，一定要保证这里正确
  languageScript.updateLangKey(node, oldLangKey as string)
  langList.forEach(langItem => {
    // 有 textKey 才需要从 languages.json 中获取文本配置
    const textFromJson = oldLangKey
      ? getTextConfigFromLanguagesJson({
        lang: langItem,
        languages: languagesRes.languages,
        textKey: oldLangKey
      })
      : ''
    // 如果能够从 language.json 中获取到该语言的文本配置（说明该 languages.json 没有文本横竖屏配置），则使用该文本
    if (textFromJson) {
      // 有些包 value 配置了 number 类型的数据，将其强行转成 string，因为引擎要求的是 string
      _.set(newConfig.langConfig, `${langItem}.pdef`, {
        ...textFromJson,
        value: String(textFromJson.value),
        // 补充名字，用于添加一整个语言包时用
        name: node.name || textFromJson.name
      })
      _.set(newConfig.langConfig, `${langItem}.ldef`, {
        ...textFromJson,
        value: String(textFromJson.value),
        name: node.name || textFromJson.name
      })
    } else {
      console.log(
        '节点的 ps.languages 脚本中有 langKey，但是没有从 language.json 读取到文本配置，使用当前节点默认的配置'
      )
      // 没有从 language.json 读取到文本配置，则使用当前节点默认的配置
      const textNodeConfig = generateLanguageConfigTextByNode(node as UIText)
      _.set(
        newConfig.langConfig,
        `${langItem}.pdef`,
        Object.assign({}, textNodeConfig)
      )
      _.set(
        newConfig.langConfig,
        `${langItem}.ldef`,
        Object.assign({}, textNodeConfig)
      )
    }
  })
}

function getAllTextKeys(languagesRes: i18n.LanguagesConfig) {
  if (!languagesRes) return []
  const languages = languagesRes.languages
  const fallbackLocale = languagesRes.fallbackLocale
  if (!languages || !fallbackLocale || !languagesRes.fallbackLocale) return []
  return Object.keys(languagesRes.languages[fallbackLocale])
}

/**
 * 找到没有关联文本节点的文本配置
 */
export function getTextKeysWhichNotRelatedToTextNode({
  languagesRes,
  gamePlayNode
}: {
  languagesRes: i18n.LanguagesConfig;
  gamePlayNode: Node;
}): string[] {
  const allTextKeys = getAllTextKeys(languagesRes)
  const textNodes = getDescendantsText(gamePlayNode)
  // 文本节点的 langKey 可能是一样的
  const textKeysWhichHadRelatedToTextNode = textNodes
    .map(node => {
      const langScript = languageScript.getScript(node, false)
      if (!langScript) {
        console.log(`${node.name} 该节点没有脚本，无法获取关联的文本配置`)
        return ''
      }
      if (!langScript.langKey) {
        console.log(`${node.name} 该节点的脚本没有 langKey，无法获取关联的文本配置`)
        return ''
      }
      return langScript.langKey
    })
    .filter(Boolean)
  const diffArr = _.difference(allTextKeys, textKeysWhichHadRelatedToTextNode)
  console.log('没有关联文本节点的文本配置有：', diffArr)
  return diffArr
}
